import { useEffect, useId, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { CredentialVaultScope } from "../lib/credential-vault-scope";
import { invalidateCredentialVaultRecord } from "../lib/credential-vault-invalidation";
import { useTRPC } from "@/trpc";
import { cn } from "@/lib/utils";
import { getProviderSpecialtyCodes } from "../lib/provider-specialties";
import type { Credential } from "../types";

export function CredentialSpecialtiesEditModal({
  scope,
  credentials,
  onClose,
}: {
  scope: CredentialVaultScope;
  credentials: Credential[];
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const inputId = useId();
  const openerRef = useRef<HTMLElement | null>(null);
  const [baseline] = useState(() => getProviderSpecialtyCodes(credentials));
  const [codes, setCodes] = useState(baseline);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isFinishing, setIsFinishing] = useState(false);
  const mutation = useMutation(
    trpc.credentials.saveMySpecialties.mutationOptions(),
  );
  const orgMutation = useMutation(
    trpc.credentials.saveSpecialtiesForBu.mutationOptions(),
  );
  const isPending = mutation.isPending || orgMutation.isPending || isFinishing;
  const isDirty =
    baseline.length !== codes.length ||
    codes.some((code) => !baseline.includes(code));

  useEffect(() => {
    if (!isDirty && !isPending) {
      return;
    }
    function preventUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
    }
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [isDirty, isPending]);

  function requestClose() {
    if (isPending) {
      return;
    }
    if (isDirty) {
      setShowDiscardWarning(true);
    } else {
      onClose();
    }
  }

  async function save() {
    if (isPending || !isDirty) {
      return;
    }
    setFormError(null);
    try {
      if (scope.kind === "organization") {
        await orgMutation.mutateAsync({
          organizationBusinessUnitId: scope.organizationBusinessUnitId,
          providerUserId: scope.providerUserId,
          ...(scope.expectedAffiliationId
            ? { expectedAffiliationId: scope.expectedAffiliationId }
            : {}),
          specialtyCodes: codes,
        });
      } else {
        await mutation.mutateAsync({
          switchUserId: scope.userId,
          specialtyCodes: codes,
        });
      }
      setIsFinishing(true);
      await invalidateCredentialVaultRecord({ queryClient, trpc, scope });
      toast.success("Specialties saved");
      onClose();
    } catch (error) {
      setIsFinishing(false);
      setFormError(
        error instanceof Error
          ? error.message
          : "Could not save your specialties. Please try again.",
      );
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          requestClose();
        }
      }}
    >
      <DialogContent
        className={PROFILE_FORM_STYLES.dialog}
        onOpenAutoFocus={() => {
          openerRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
        }}
        onCloseAutoFocus={(event) => {
          const parentDialog = Array.from(
            document.querySelectorAll<HTMLElement>(
              '[role="dialog"][data-state="open"]',
            ),
          ).at(-1);
          if (!parentDialog) {
            return;
          }
          event.preventDefault();
          if (
            openerRef.current?.isConnected &&
            parentDialog.contains(openerRef.current)
          ) {
            openerRef.current.focus();
          } else {
            parentDialog.focus();
          }
        }}
      >
        <DialogHeader className={PROFILE_FORM_STYLES.header}>
          <div className="flex items-start gap-4">
            <div className={PROFILE_FORM_STYLES.icon}>
              <Stethoscope className="size-5" aria-hidden="true" />
            </div>
            <div className="min-w-0 space-y-1.5">
              <p className={PROFILE_FORM_STYLES.eyebrow}>
                {scope.kind === "organization"
                  ? "Professional details"
                  : "Your profile"}
              </p>
              <DialogTitle className={PROFILE_FORM_STYLES.title}>
                Edit specialties
              </DialogTitle>
              <DialogDescription className={PROFILE_FORM_STYLES.description}>
                Choose the specialties to include on this professional record.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className={cn(PROFILE_FORM_STYLES.body, "h-80")}>
            <section className={PROFILE_FORM_STYLES.section}>
              <h3 className={PROFILE_FORM_STYLES.sectionHeading}>
                Areas of practice
              </h3>
              <div className="space-y-2">
                <Label htmlFor={inputId} className={PROFILE_FORM_STYLES.label}>
                  Specialties
                </Label>
                <SpecialtiesSelect
                  inputId={inputId}
                  appearance="profile"
                  isMulti
                  value={codes}
                  disabled={isPending}
                  placeholder="Search and select specialties"
                  onChange={(value) => {
                    setCodes(Array.isArray(value) ? value : []);
                    setFormError(null);
                  }}
                />
                <p className="text-xs leading-relaxed text-slate-500">
                  Select all that apply. Remove a selection to take it off this
                  record.
                </p>
              </div>
            </section>
            {formError && (
              <p role="alert" className={PROFILE_FORM_STYLES.error}>
                {formError}
              </p>
            )}
          </div>
          <DialogFooter className={PROFILE_FORM_STYLES.footer}>
            <Button
              type="button"
              variant="outline"
              className={PROFILE_FORM_STYLES.secondaryButton}
              disabled={isPending}
              onClick={requestClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className={PROFILE_FORM_STYLES.primaryButton}
              disabled={isPending || !isDirty}
            >
              {isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
        <AlertDialog
          open={showDiscardWarning}
          onOpenChange={setShowDiscardWarning}
        >
          <AlertDialogContent className={PROFILE_FORM_STYLES.confirmation}>
            <AlertDialogHeader className="text-left">
              <AlertDialogTitle className={PROFILE_FORM_STYLES.title}>
                Discard your changes?
              </AlertDialogTitle>
              <AlertDialogDescription
                className={PROFILE_FORM_STYLES.description}
              >
                Your specialty selections have not been saved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-2 gap-3 sm:space-x-0">
              <AlertDialogCancel
                className={cn(PROFILE_FORM_STYLES.secondaryButton, "mt-0")}
              >
                Keep editing
              </AlertDialogCancel>
              <AlertDialogAction
                className={PROFILE_FORM_STYLES.dangerButton}
                onClick={onClose}
              >
                Discard changes
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
